// Cómo se escribe un monto. Un solo lugar, porque hasta M1 había tres.
//
// M1 de `odd/tasks/multimoneda.md`.
//
// LO QUE HABÍA
// ------------
// `const ars = n => "$" + Math.round(n||0).toLocaleString("es-AR")`, copiado
// byte por byte en cinco archivos y llamado 300 veces; unos 30
// `.toLocaleString("es-AR")` sueltos que ni pasaban por ahí; y `plata()` en
// `Casino.jsx`, que además dividía por 100. Tres convenciones para la misma
// pregunta.
//
// LA UNIDAD VA EN EL NOMBRE, NO EN UNA BANDERA
// ---------------------------------------------
// Por eso son dos funciones y no una con un `{centavos:true}`. El 2026-10-20
// se arreglaron cuatro defectos de 100× en un día, todos de la misma familia:
// un `bigint` que lleva centavos y otro que lleva pesos, sin que el tipo ni el
// nombre lo digan (ver `odd/tasks/el-registro-de-unidades.md`). Una bandera
// que se puede olvidar es exactamente esa enfermedad. `money` recibe unidades
// enteras y `moneyDeCentavos` recibe centavos, y eso se lee en la llamada.
//
// EL SÍMBOLO EQUIVOCADO NO SE VE EQUIVOCADO
// ------------------------------------------
// Es la razón de que una moneda desconocida NO caiga a `"$"`, que es lo que
// hacía `money()` en `App.jsx`. Mostrar `$500` para una moneda que no
// conocemos se lee como pesos y miente; mostrar `XYZ 500` se lee raro, que es
// lo correcto cuando algo anda mal. Mismo criterio que `fechas.js` con una
// fecha que no es ISO: se descarta en vez de dibujar otra con cara de buena.
//
// Y CUATRO MONEDAS COMPARTEN EL `$`
// ----------------------------------
// ARS, CLP, COP y MXN. Dentro de una agencia da igual, porque tiene una sola
// moneda. En una lista con varias —el panel de admin— `$500` y `$500` pueden
// ser dos cosas distintas, y por eso existe `{conCodigo:true}`. DÓNDE se usa
// lo decide M6; acá sólo se provee.
//
// ARS NO CAMBIA
// -------------
// Garantía del módulo, y la que hace seguro un cambio de 300 sitios: todo
// monto en pesos se dibuja exactamente igual que antes de M1. Está fijado en
// `moneda.test.js` contra la fórmula vieja, literal.

// Símbolo, decimales y locale de cada moneda.
//
// Los decimales no son un detalle de estilo: `Math.round` —lo que hacía la
// fórmula vieja— convierte US$ 12,50 en US$ 13. En guaraníes no existe el
// centavo y redondear está bien; en dólares es plata que desaparece al
// dibujarla.
//
// El locale queda en `es-AR` para todas MENOS el dólar. No es pereza: es la
// forma que esas pantallas ya tienen hoy, y cambiarla sería un cambio visual
// que nadie pidió, mezclado con uno que sí. El dólar es la excepción porque
// `es-AR` lo escribiría `1.234,56`, y un precio en dólares se lee
// `1,234.56` en Ecuador y en todos lados.
const MONEDAS = {
  ARS: { simbolo: "$",   decimales: 0, locale: "es-AR" },
  CLP: { simbolo: "$",   decimales: 0, locale: "es-AR" },
  COP: { simbolo: "$",   decimales: 0, locale: "es-AR" },
  PYG: { simbolo: "₲",   decimales: 0, locale: "es-AR" },
  USD: { simbolo: "US$", decimales: 2, locale: "en-US" },
  EUR: { simbolo: "€",   decimales: 2, locale: "es-AR" },
  BRL: { simbolo: "R$",  decimales: 2, locale: "es-AR" },
  UYU: { simbolo: "$U",  decimales: 2, locale: "es-AR" },
  PEN: { simbolo: "S/",  decimales: 2, locale: "es-AR" },
  MXN: { simbolo: "$",   decimales: 2, locale: "es-AR" },
  // BOB y VES comparten "Bs" y hoy ninguna está en juego. Se deja anotado en
  // vez de inventarle una distinción a algo que nadie usa.
  BOB: { simbolo: "Bs",  decimales: 2, locale: "es-AR" },
  VES: { simbolo: "Bs",  decimales: 2, locale: "es-AR" },
};

const POR_DEFECTO = "ARS";

export function codigoDeMoneda(moneda) {
  return String(moneda || POR_DEFECTO).trim().toUpperCase();
}

export function conoceLaMoneda(moneda) {
  return Object.prototype.hasOwnProperty.call(MONEDAS, codigoDeMoneda(moneda));
}

export function decimalesDe(moneda) {
  const m = MONEDAS[codigoDeMoneda(moneda)];
  return m ? m.decimales : 2;
}

export function simboloDe(moneda) {
  const m = MONEDAS[codigoDeMoneda(moneda)];
  return m ? m.simbolo : null;
}

function numero(n) {
  const x = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(x) ? x : 0;
}

// Se redondea A LA PRECISIÓN DE LA MONEDA antes de dibujar, y con `Math.round`
// a propósito: en una moneda de cero decimales eso ES la fórmula vieja,
// carácter por carácter, que es lo que hace seguro cambiar 300 sitios de una.
// En una de dos decimales redondea al centavo, que es lo mismo que hace el
// backend con `ROUND_HALF_UP` (`bot/atomic.py`).
//
// Difieren en un solo caso, el medio negativo: `Math.round(-0.5)` da `-0` y el
// backend daría `-1`. Queda anotado y no se corrige acá: emparejarlo cambiaría
// cómo se ve hoy un saldo en pesos, que es justo lo que M1 promete no tocar.
function redondear(x, decimales) {
  const escala = Math.pow(10, decimales);
  return Math.round(x * escala) / escala;
}

/**
 * Escribe un monto que viene en UNIDADES ENTERAS de su moneda.
 *
 * `conCodigo` agrega el código después del número, para listas donde conviven
 * varias monedas y el símbolo no alcanza para distinguirlas.
 */
export function money(n, moneda, { conCodigo = false } = {}) {
  const codigo = codigoDeMoneda(moneda);
  const m = MONEDAS[codigo];
  const x = numero(n);

  // Moneda que no conocemos: se nombra en vez de disfrazarse de pesos.
  if (!m) {
    const cuerpo = x.toLocaleString("es-AR", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    return `${codigo} ${cuerpo}`;
  }

  const cuerpo = redondear(x, m.decimales).toLocaleString(m.locale, {
    minimumFractionDigits: m.decimales,
    maximumFractionDigits: m.decimales,
  });
  return conCodigo ? `${m.simbolo}${cuerpo} ${codigo}` : `${m.simbolo}${cuerpo}`;
}

/**
 * Escribe un monto que viene en CENTAVOS.
 *
 * La división vive acá y no en cada pantalla porque ése fue el origen de los
 * cuatro defectos de 100×: la conversión copiada a mano en más de treinta
 * lectores. En una moneda sin centavos el resultado se redondea igual, que es
 * lo que corresponde.
 */
export function moneyDeCentavos(n, moneda, opciones) {
  return money(numero(n) / 100, moneda, opciones);
}
